/**
 * demux.js
 *
 * fMP4 box-level demuxer (plain JS, no library) for the WebCodecs/WASM
 * decode paths. Parses just enough ISO BMFF to turn each `moof`+`mdat` pair
 * into HEVC Annex B access units with an absolute PTS.
 *
 * PORTED AS-IS from the mock FE's `Demux` IIFE
 * (vgps-cam4g-ssr910q/tools/host/playback-sandbox/mock_fe/index.html,
 * function `Demux`) per PLAYBACK-HANDOFF.md §8 point 2: "The demuxer is
 * ~300 lines of plain JS in the mock FE (`Demux`)... verified against
 * `ffprobe -show_frames` (frame count + first/last pts) before it was wired
 * to a decoder. Port it as-is — do not rewrite it, and do not add an mp4
 * library for this." Only the module wrapper changed (IIFE -> ES exports);
 * every function body below is unchanged.
 *
 * Box layout facts this relies on (checked against the real fixtures): the
 * init chunk is `ftyp`+`moov` only; each media chunk file starts at byte 0
 * with its own `moof` (the device cuts on moof boundaries, so `tfhd`'s
 * default-base-is-moof flag makes `trun.data_offset` relative to THIS
 * file's start, not a track base); a chunk can hold several moof+mdat pairs
 * back to back.
 *
 * NOTE: the mock FE's separate `Integrity` module (K1-K7 segment integrity
 * check) reuses these same box-walking primitives to answer a different
 * question ("is anything missing?") — per handoff §10 ("What NOT to port"),
 * the integrity checker itself is sandbox-only and is NOT ported here.
 */

function u32(dv, o) { return dv.getUint32(o, false); }
function i32(dv, o) { return dv.getInt32(o, false); }
function u64(dv, o) { return dv.getUint32(o, false) * 4294967296 + dv.getUint32(o + 4, false); }

export function iterBoxes(bytes, dv, start, end) {
  const out = [];
  let i = start;
  while (i + 8 <= end) {
    let size = u32(dv, i);
    const type = String.fromCharCode(bytes[i + 4], bytes[i + 5], bytes[i + 6], bytes[i + 7]);
    let headerLen = 8;
    if (size === 1) { size = u64(dv, i + 8); headerLen = 16; }
    else if (size === 0) { size = end - i; }
    if (size < headerLen || i + size > end) break; // truncated tail -- ignore, caller retries later
    out.push({ type: type, start: i, bodyStart: i + headerLen, end: i + size });
    i += size;
  }
  return out;
}
export function findBox(boxes, type) {
  for (let i = 0; i < boxes.length; i++) if (boxes[i].type === type) return boxes[i];
  return null;
}
export function nalType(byte0) { return (byte0 >> 1) & 0x3f; }
export function isKeyNalType(t) { return t >= 16 && t <= 21; } // HEVC IDR_W_RADL..RSV_IRAP_VCL23

function concatChunks(parts) {
  let total = 0;
  for (let i = 0; i < parts.length; i++) total += parts[i].length;
  const out = new Uint8Array(total);
  let o = 0;
  for (let i = 0; i < parts.length; i++) { out.set(parts[i], o); o += parts[i].length; }
  return out;
}

// Init segment (chunk seq 0: ftyp+moov, zero samples) -> timescale, coded
// width/height, NAL length size, VPS+SPS+PPS as one Annex-B blob (prepended
// to every keyframe access unit -- required in-band for both WebCodecs
// without a `description` and h265web.js's live NALU feed).
export function parseInit(bytes) {
  const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const top = iterBoxes(bytes, dv, 0, bytes.length);
  const moov = findBox(top, 'moov');
  if (!moov) throw new Error('init chunk has no moov');
  const trak = findBox(iterBoxes(bytes, dv, moov.bodyStart, moov.end), 'trak');
  const mdia = findBox(iterBoxes(bytes, dv, trak.bodyStart, trak.end), 'mdia');
  const mdhd = findBox(iterBoxes(bytes, dv, mdia.bodyStart, mdia.end), 'mdhd');
  const mdhdVer = bytes[mdhd.bodyStart];
  const timescale = mdhdVer === 1 ? u32(dv, mdhd.bodyStart + 20) : u32(dv, mdhd.bodyStart + 12);
  const minf = findBox(iterBoxes(bytes, dv, mdia.bodyStart, mdia.end), 'minf');
  const stbl = findBox(iterBoxes(bytes, dv, minf.bodyStart, minf.end), 'stbl');
  const stsd = findBox(iterBoxes(bytes, dv, stbl.bodyStart, stbl.end), 'stsd');
  const entryBoxes = iterBoxes(bytes, dv, stsd.bodyStart + 8, stsd.end); // fullbox hdr(4)+entry_count(4)
  const sampleEntry = entryBoxes[0];
  if (!sampleEntry || (sampleEntry.type !== 'hvc1' && sampleEntry.type !== 'hev1')) {
    throw new Error('stsd sample entry is not hvc1/hev1: ' + (sampleEntry && sampleEntry.type));
  }
  // VisualSampleEntry fixed fields up to (but not including) the codec
  // config box: reserved(6)+data_ref_idx(2)+pre_defined(2)+reserved(2)
  // +pre_defined(12)+width(2)+height(2)+horiz(4)+vert(4)+reserved(4)
  // +frame_count(2)+compressorname(32)+depth(2)+pre_defined(2) = 78B.
  let p = sampleEntry.bodyStart + 6 + 2 + 2 + 2 + 12;
  const width = dv.getUint16(p, false); p += 2;
  const height = dv.getUint16(p, false); p += 2;
  p += 4 + 4 + 4 + 2 + 32 + 2 + 2;
  const hvcC = findBox(iterBoxes(bytes, dv, p, sampleEntry.end), 'hvcC');
  if (!hvcC) throw new Error('sample entry has no hvcC');
  const hb = hvcC.bodyStart;
  const lengthSize = (bytes[hb + 21] & 0x3) + 1; // lengthSizeMinusOne
  const numArrays = bytes[hb + 22];
  let off = hb + 23;
  const paramParts = [];
  for (let a = 0; a < numArrays; a++) {
    off += 1; // array_completeness(1)+reserved(1)+nal_unit_type(6)
    const numNalus = dv.getUint16(off, false); off += 2;
    for (let n = 0; n < numNalus; n++) {
      const nalLen = dv.getUint16(off, false); off += 2;
      paramParts.push(new Uint8Array([0, 0, 0, 1]));
      paramParts.push(bytes.subarray(off, off + nalLen));
      off += nalLen;
    }
  }
  return {
    timescale: timescale, width: width, height: height, lengthSize: lengthSize,
    paramSetsAnnexB: concatChunks(paramParts),
  };
}

function lengthPrefixedToAnnexB(sample, lengthSize) {
  const parts = [];
  let i = 0;
  const n = sample.length;
  while (i + lengthSize <= n) {
    let len = 0;
    for (let k = 0; k < lengthSize; k++) len = (len << 8) | sample[i + k];
    i += lengthSize;
    parts.push(new Uint8Array([0, 0, 0, 1]));
    parts.push(sample.subarray(i, i + len));
    i += len;
  }
  return concatChunks(parts);
}
function annexBHasKeyframeNal(annexB) {
  for (let i = 0; i + 4 < annexB.length; i++) {
    if (annexB[i] === 0 && annexB[i + 1] === 0 && annexB[i + 2] === 0 && annexB[i + 3] === 1) {
      if (isKeyNalType(nalType(annexB[i + 4]))) return true;
      i += 3;
    }
  }
  return false;
}

// One media chunk file -> ordered access units [{annexB, isKey, ptsUs, size}].
// May contain several moof+mdat pairs (the device flushes a chunk once its
// accumulated moof+mdat groups reach the target chunk size).
export function parseMediaChunk(bytes, initInfo) {
  const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const top = iterBoxes(bytes, dv, 0, bytes.length);
  const out = [];
  for (let bi = 0; bi < top.length; bi++) {
    const box = top[bi];
    if (box.type !== 'moof') continue;
    const moofStart = box.start;
    const traf = findBox(iterBoxes(bytes, dv, box.bodyStart, box.end), 'traf');
    if (!traf) continue;
    const inner = iterBoxes(bytes, dv, traf.bodyStart, traf.end);
    const tfdt = findBox(inner, 'tfdt');
    const trun = findBox(inner, 'trun');
    if (!tfdt || !trun) continue;
    const tfdtVer = bytes[tfdt.bodyStart];
    const baseMediaDecodeTime = tfdtVer === 1 ? u64(dv, tfdt.bodyStart + 4) : u32(dv, tfdt.bodyStart + 4);

    const trunVerFlags = u32(dv, trun.bodyStart);
    const trunVer = trunVerFlags >>> 24;
    const trunFlags = trunVerFlags & 0xffffff;
    const sampleCount = u32(dv, trun.bodyStart + 4);
    let tp = trun.bodyStart + 8;
    let dataOffset = 0;
    if (trunFlags & 0x000001) { dataOffset = i32(dv, tp); tp += 4; }
    if (trunFlags & 0x000004) { tp += 4; } // first_sample_flags -- unused, NAL type gates keyframe
    let mdatCursor = moofStart + dataOffset; // default-base-is-moof (tfhd flags 0x020000)
    let cumDurationUnits = 0;
    for (let si = 0; si < sampleCount; si++) {
      let duration = 0, size = 0, cto = 0;
      if (trunFlags & 0x000100) { duration = u32(dv, tp); tp += 4; }
      if (trunFlags & 0x000200) { size = u32(dv, tp); tp += 4; }
      if (trunFlags & 0x000400) { tp += 4; } // sample_flags -- unused
      if (trunFlags & 0x000800) { cto = trunVer === 1 ? i32(dv, tp) : u32(dv, tp); tp += 4; }
      const raw = bytes.subarray(mdatCursor, mdatCursor + size);
      const annexB = lengthPrefixedToAnnexB(raw, initInfo.lengthSize);
      const isKey = annexBHasKeyframeNal(annexB);
      const ptsUnits = baseMediaDecodeTime + cumDurationUnits + cto;
      const ptsUs = Math.round((ptsUnits * 1e6) / initInfo.timescale);
      out.push({
        annexB: isKey ? concatChunks([initInfo.paramSetsAnnexB, annexB]) : annexB,
        isKey: isKey, ptsUs: ptsUs, size: size,
      });
      mdatCursor += size;
      cumDurationUnits += duration;
    }
  }
  return out;
}
